import { Component, ViewEncapsulation, HostBinding, OnInit } from '@angular/core';
import { OperatorExpression,
	toString as luceneToString } from 'lucene';
import { map, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { Guarantee } from '../../../guarantee/models/guarantee';
import { GuaranteeStatus } from '../../../guarantee/models/guarantee-constants';
import { GuaranteeRequestService, REQUEST_FIELDS } from '../../../guarantee/services/guarantee-request.service';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { TranslationService } from '../../../shared/services/translation.service';
import { OrderBy, OrderByDirection } from '../../../shared/utils/constant';
import { getGuaranteeRequestStatusTr } from '../../../guarantee/utils/guarantee.utils';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';

@Component({
	selector: 'copo-last-guarantee-requests-widget',
	templateUrl: './last-guarantee-requests-widget.component.html',
	styleUrl: './last-guarantee-requests-widget.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		NgIf,
		NgFor,

		MatButtonModule,
		RouterLink,
		MatIconModule,
		LoadingSpinnerComponent,
		DatePipe
	]
})

export class LastGuaranteeRequestsWidgetComponent implements OnInit {
	@HostBinding('class.card-block') block = true;

	isLoading = true;
	guaranteeSub?: Subscription;

	guaranteeRequests?: Guarantee[];

	constructor(
		private readonly guaranteeRequestService: GuaranteeRequestService,
		private readonly translationService: TranslationService
	) {}

	ngOnInit() {
		this.guaranteeSub = this.guaranteeRequestService.getRequests(
			REQUEST_FIELDS,
			this.createSearchFilter(),
			1,
			4,
			[new OrderBy('requestDate', OrderByDirection.DESC)],
			false,
			false
		).pipe(map(res => res.results))
			.subscribe({
				next: guaranteeRequestsResults => {
					this.guaranteeRequests = guaranteeRequestsResults;
					this.isLoading = false;
				},
				error: (err: unknown) => {
					console.error('error loading', err);
					this.isLoading = false;
				}
			});
	}

	createSearchFilter() {
		const notDraftNode: OperatorExpression = {
			start: 'NOT',
			left: {
				field: 'status',
				term: 'Draft',
				quoted: true
			}
		};
		return luceneToString(notDraftNode);
	}

	isDraft(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.DRAFT;
	}

	isOpen(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.OPEN;
	}

	isOther(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.LAUNCH_ANALYSIS;
	}

	isSatisfied(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.SATISFIED;
	}

	isCertificateRequested(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.CERTIFICATE_REQUESTED;
	}

	isCertificateIssued(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.CERTIFICATE_ISSUED;
	}

	isRejected(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.REJECTED_BY_SUPLIER;
	}

	isCancelled(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.CANCELLED;
	}

	isUnknown(guarantee: Guarantee) {
		return !this.isDraft(guarantee)
			&& !this.isOpen(guarantee)
			&& !this.isOther(guarantee)
			&& !this.isSatisfied(guarantee)
			&& !this.isCertificateRequested(guarantee)
			&& !this.isCertificateIssued(guarantee)
			&& !this.isRejected(guarantee)
			&& !this.isCancelled(guarantee);
	}

	getGuaranteeRequestStatus(status: GuaranteeStatus) {
		return getGuaranteeRequestStatusTr(this.translationService, status);
	}
}
